import Plugin from "@ckeditor/ckeditor5-core/src/plugin";
import EmitterMixin from "@ckeditor/ckeditor5-utils/src/emittermixin";
import mix from "@ckeditor/ckeditor5-utils/src/mix";

/**
 * Looks at the changes to see if the element was moved within the document
 * rather than actually deleted
 */
const elementMoved = (changes, elementType, contentRepositoryAttachableId) => {
  // If there was an insert event for the same element, it was moved and not deleted
  return !!changes.find(
    ({ type, name, attributes }) =>
      type === "insert" &&
      name === elementType &&
      attributes.get("content-repository-attachable-id") ===
        contentRepositoryAttachableId
  );
};

/**
 * Checks if the element was converted from a block image to an inline image rather
 * than being actually deleted
 */
const convertedToInlineImg = (
  changes,
  elementType,
  contentRepositoryAttachableId
) => {
  return (
    elementType === "imageBlock" &&
    !!changes.find(({ type, name, position, attributes }) => {
      // Indicates that there was a possible re-insertion of the image
      const isInsert = type === "insert";

      // Occurs when the user clicks on the inline button on the image
      const convertedToInline =
        type === "insert" &&
        name === "paragraph" &&
        position.nodeAfter
          ?.getChild(0)
          ?.getAttribute("content-repository-attachable-id") ===
          contentRepositoryAttachableId;

      // Occurs when the image is made inline because it was moved within the document
      const movedInline =
        name === "imageInline" &&
        attributes.get("content-repository-attachable-id") ===
          contentRepositoryAttachableId;

      return isInsert && (convertedToInline || movedInline);
    })
  );
};

/**
 * Checks if the element was converted from an inline image to a block image rather
 * than being actually deleted
 */
const convertedToBlockImg = (
  changes,
  elementType,
  contentRepositoryAttachableId
) => {
  return (
    elementType === "imageInline" &&
    !!changes.find(({ type, name, attributes }) => {
      // Indicates that there was a possible re-insertion of the image
      const isInsert = type === "insert";

      // Occurs when the user moves an image and it becomes a block image
      const convertedToBlock =
        name === "imageBlock" &&
        attributes.get("content-repository-attachable-id") ===
          contentRepositoryAttachableId;

      return isInsert && convertedToBlock;
    })
  );
};

const filterAttachableElements = (node) => {
  if (
    node.getAttribute &&
    node.getAttribute("data-content-repository-attachable-id")
  ) {
    node.parent?._removeChildren(node.parent?.getChildIndex(node));
  } else if (node.getChildren) {
    [...node.getChildren()]?.forEach(filterAttachableElements);
  }
};

/**
 * A really hacky way of handling deletion of attachables and preventing
 * copying and pasting elements with content repository attachable IDs.
 *
 * This plugin should be removed once collaboration is implemented.
 */
class ContentRepositoryAttachablesPlugin extends Plugin {
  static get requires() {
    return [];
  }

  init() {
    this.listenForDeletedAttachables();
    this.preventCopyAndPaste();
  }

  preventCopyAndPaste() {
    this.editor.plugins
      .get("ClipboardPipeline")
      .on("inputTransformation", (evt, data) => {
        if (data.method === "paste") {
          filterAttachableElements(data.content);
        }
      });
  }

  listenForDeletedAttachables() {
    const elementTypes = [
      "imageInline",
      "imageBlock",
      "trainingLink",
      "repositoryItemLink",
      "embeddedVideo",
    ];

    this.editor.model.document.on("change:data", (event) => {
      const differ = event.source.differ;

      if (differ.isEmpty) {
        return;
      }

      const changes = differ.getChanges({
        includeChangesInGraveyard: false,
      });

      if (changes.length === 0) {
        return;
      }

      const removedNodes = changes.filter(({ type }) => type === "remove");

      let removedAttachableIds = [];
      removedNodes.forEach((node) => {
        if (elementTypes.includes(node.name)) {
          const contentRepositoryAttachableId = node.attributes.get(
            "content-repository-attachable-id"
          );

          const shouldKeep =
            elementMoved(changes, node.name, contentRepositoryAttachableId) ||
            convertedToInlineImg(
              changes,
              node.name,
              contentRepositoryAttachableId
            ) ||
            convertedToBlockImg(
              changes,
              node.name,
              contentRepositoryAttachableId
            );

          if (!shouldKeep) {
            removedAttachableIds.push(contentRepositoryAttachableId);
          }
        }
      });

      removedAttachableIds.forEach((attachableId) =>
        this.editor.fire("removeAttachable", attachableId)
      );
    });
  }
}

mix(ContentRepositoryAttachablesPlugin, EmitterMixin);

export default ContentRepositoryAttachablesPlugin;
